import { z } from "zod"

import {
  branchIdSchema,
  commandIdSchema,
  correlationIdSchema,
  entityIdSchema,
  identityIdSchema,
  legalEntityIdSchema,
  membershipIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

const idempotencyKeySchema = z
  .string()
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/,
    "Idempotency key must use 1-128 portable ASCII characters."
  )

const commandTypeSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+$/,
    "Command type must be a lowercase, namespaced identifier."
  )

const entityTypeSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9_]{0,63}$/,
    "Entity type must be a lowercase identifier."
  )

const commandContextV1Schema = z
  .object({
    legalEntityId: legalEntityIdSchema.nullable(),
    branchId: branchIdSchema.nullable(),
    entityType: entityTypeSchema.nullable(),
    entityId: entityIdSchema.nullable(),
  })
  .strict()
  .superRefine((context, refinement) => {
    if (context.branchId !== null && context.legalEntityId === null) {
      refinement.addIssue({
        code: "custom",
        path: ["legalEntityId"],
        message: "BRANCH_REQUIRES_LEGAL_ENTITY",
      })
    }

    if ((context.entityType === null) !== (context.entityId === null)) {
      refinement.addIssue({
        code: "custom",
        path: [context.entityType === null ? "entityType" : "entityId"],
        message: "ENTITY_CONTEXT_PAIR_REQUIRED",
      })
    }
  })

const commandActorV1Schema = z
  .object({
    identityId: identityIdSchema,
    membershipId: membershipIdSchema,
  })
  .strict()

/**
 * Version 1 of the canonical ImpetusOS command wire envelope.
 *
 * Context fields and expectedVersion are required even when their value is null. Null
 * means "not applicable" or "no existing version"; omission is always malformed.
 */
export const commandEnvelopeV1Schema = z
  .object({
    envelopeVersion: z.literal(1),
    commandId: commandIdSchema,
    idempotencyKey: idempotencyKeySchema,
    tenantId: tenantIdSchema,
    context: commandContextV1Schema,
    actor: commandActorV1Schema,
    correlationId: correlationIdSchema,
    expectedVersion: z.number().int().nonnegative().safe().nullable(),
    commandType: commandTypeSchema,
    commandVersion: z.number().int().positive().safe(),
    payload: z.record(z.string(), z.json()),
  })
  .strict()

export type CommandContextV1 = z.infer<typeof commandContextV1Schema>
export type CommandActorV1 = z.infer<typeof commandActorV1Schema>
export type CommandEnvelopeV1 = z.infer<typeof commandEnvelopeV1Schema>

export type CommandEnvelopeValidationCode =
  | "INVARIANT"
  | "INVALID_FORMAT"
  | "INVALID_TYPE"
  | "INVALID_VALUE"
  | "OUT_OF_RANGE"
  | "REQUIRED"
  | "UNKNOWN_FIELD"

export interface CommandEnvelopeValidationIssue {
  readonly code: CommandEnvelopeValidationCode
  readonly path: string
  readonly message: string
}

export type CommandEnvelopeValidationResult =
  | { readonly ok: true; readonly value: CommandEnvelopeV1 }
  | { readonly ok: false; readonly issues: readonly CommandEnvelopeValidationIssue[] }

function readPath(input: unknown, path: readonly PropertyKey[]): unknown {
  let current = input

  for (const segment of path) {
    if (typeof current !== "object" || current === null) return undefined
    current = Reflect.get(current, segment)
  }

  return current
}

function displayPath(path: readonly PropertyKey[]): string {
  return path.length === 0 ? "$" : path.map(String).join(".")
}

function stableIssue(
  code: CommandEnvelopeValidationCode,
  path: readonly PropertyKey[],
  message: string
): CommandEnvelopeValidationIssue {
  return { code, path: displayPath(path), message }
}

function normalizeIssue(input: unknown, issue: z.core.$ZodIssue): CommandEnvelopeValidationIssue[] {
  if (issue.code === "unrecognized_keys") {
    return [...issue.keys]
      .sort()
      .map((key) =>
        stableIssue("UNKNOWN_FIELD", [...issue.path, key], "Field is not part of envelope version 1.")
      )
  }

  if (issue.code === "custom") {
    const message =
      issue.message === "BRANCH_REQUIRES_LEGAL_ENTITY"
        ? "Branch context requires Legal Entity context."
        : "Entity type and entity identifier must be supplied together."
    return [stableIssue("INVARIANT", issue.path, message)]
  }

  if (readPath(input, issue.path) === undefined) {
    return [stableIssue("REQUIRED", issue.path, "Required field is missing.")]
  }

  if (issue.code === "invalid_type") {
    return [
      stableIssue("INVALID_TYPE", issue.path, "Field has the wrong JSON type."),
    ]
  }

  if (issue.code === "invalid_format") {
    return [stableIssue("INVALID_FORMAT", issue.path, "Field does not match its canonical format.")]
  }

  if (issue.code === "too_big" || issue.code === "too_small") {
    return [stableIssue("OUT_OF_RANGE", issue.path, "Numeric field is outside its allowed range.")]
  }

  return [stableIssue("INVALID_VALUE", issue.path, "Field contains an unsupported value.")]
}

/** Validate unknown wire input without leaking payload values or library-specific errors. */
export function validateCommandEnvelopeV1(input: unknown): CommandEnvelopeValidationResult {
  const result = commandEnvelopeV1Schema.safeParse(input)
  if (result.success) return { ok: true, value: result.data }

  const issues = result.error.issues
    .flatMap((issue) => normalizeIssue(input, issue))
    .sort((left, right) => {
      if (left.path < right.path) return -1
      if (left.path > right.path) return 1
      if (left.code < right.code) return -1
      if (left.code > right.code) return 1
      return 0
    })

  return { ok: false, issues }
}
